import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/sharp-solid-svg-icons';
import { ConfirmationModal } from 'eone-ui/common-modals/confirmation-modal';
import { Modal } from 'eone-ui/modal/modal';
import type { ReactNode } from 'react';
import React from 'react';
import { SCComponentRoot } from '../component_root';
import { SCErrorBoundary } from '../error_boundary';

// Temporary
interface SCModalProps {
  Title?: string;
  Show?: boolean;
  Width?: number;
  DataTestId?: string;
  FooterButtons?: {
    Label: string;
    OnClick: () => void;
    Alignment?: 1 | 2;
    Type?: number;
    Visible: boolean;
    Disabled?: boolean;
  }[];
  // When true, the modal's footer chrome (border-top + padding) is not rendered. Useful when the
  // body renders its own action bar.
  HideFooter?: boolean;
  children?: ReactNode;
  Close?: () => void;
}

interface SCConfirmationModalProps extends SCModalProps {
  Title?: string;
  Message?: string | ReactNode;
  ConfirmLabel?: string;
  CancelLabel?: string;
  ConfirmType?: number;
  Data?: any;
  OnConfirm?: (data: any) => void;
  OnCancel?: () => void;
  HideCancel?: boolean;
}

let modalId = 0;
const baseRenderModal = <P extends SCModalProps>(
  ModalComponent: React.ComponentType<P>,
  modalProps: P,
) => {
  const key = `sc_modal_${modalId++}`;

  function handleClose() {
    if (modalProps.Close) {
      modalProps.Close();
    }

    SCComponentRoot.renderComponent(key, null);
  }

  // Only the body is wrapped, so the header, close button and footer survive a crash in the
  // content and the user can still dismiss the modal. Without this, a throw in modal content
  // unmounts SCComponentRoot, whose cleanup resets renderComponent to a no-op - every modal opened
  // after that renders nothing until the page is reloaded (SC-830, SC-1360).
  // Left untouched when there are no children, so modals that key layout off `children` being
  // absent (ConfirmationModal renders `Message` instead) behave exactly as before.
  function guard(children: ReactNode) {
    if (children === undefined || children === null) {
      return children;
    }

    return <SCErrorBoundary DataTestId={modalProps.DataTestId}>{children}</SCErrorBoundary>;
  }

  const newProps = {
    Show: true,
    ...modalProps,
    children: guard(modalProps.children),
    Close: handleClose,
  };

  SCComponentRoot.renderComponent(key, <ModalComponent {...newProps} />);

  function update(updateModalProps: SCModalProps) {
    const updatedProps =
      'children' in updateModalProps
        ? { ...updateModalProps, children: guard(updateModalProps.children) }
        : updateModalProps;

    SCComponentRoot.renderComponent(key, <ModalComponent {...newProps} {...updatedProps} />);
  }

  function destroy() {
    SCComponentRoot.renderComponent(key, null);
  }

  return {
    update,
    destroy,
  };
};

const SCModal = {
  showModal: (modalProps: SCModalProps) => {
    return baseRenderModal(Modal, modalProps);
  },
  confirm: (modalProps: SCConfirmationModalProps) => {
    const result = baseRenderModal(ConfirmationModal, {
      ...modalProps,
      OnConfirm: (data: any) => {
        modalProps.OnConfirm?.(data);
        result.destroy();
      },
      OnCancel: modalProps.OnCancel
        ? () => {
            modalProps.OnCancel?.();
            result.destroy();
          }
        : undefined,
      Close: () => {
        modalProps.Close?.();
        result.destroy();
      },
    });
    return result;
  },
  alert: (modalProps: Omit<SCConfirmationModalProps, 'Close'>) => {
    const result = SCModal.confirm({
      ...modalProps,
      Title: modalProps.Title || 'Alert',
      ConfirmLabel: modalProps.ConfirmLabel || 'OK',
      HideCancel: true,
      OnConfirm: (data: any) => {
        modalProps.OnConfirm?.(data);
        result.destroy();
      },
      Close: () => {
        modalProps.OnConfirm?.(modalProps.Data);
        result.destroy();
      },
    });
    return result;
  },
};

const SCBaseModal = (props: SCConfirmationModalProps) => {
  const { Title, children, Close, FooterButtons } = props;
  const visibleFooterButtons = FooterButtons?.filter((b) => b.Visible) ?? [];
  return (
    <div className="modal-root">
      <div className="eone-ui-modal-container fade-in">
        <div className="eone-ui-modal eone-ui-modal-extra-wide">
          <div className="eone-ui-modal-header">
            <div className="eone-ui-modal-header-buttons" />
            <div className="eone-ui-modal-header-title">{Title}</div>
            <div className="eone-ui-modal-close-button" onClick={Close}>
              <FontAwesomeIcon icon={faClose} className="eone-ui-modal-close-button-icon" />
            </div>
          </div>
          <div
            className="eone-ui-modal-body"
            style={
              visibleFooterButtons.length > 0
                ? { flex: 1, minHeight: 0, overflow: 'auto' }
                : undefined
            }
          >
            {children}
          </div>
          {visibleFooterButtons.length > 0 && (
            <div className="eone-ui-modal-footer">
              <div className="eone-ui-modal-footer-button-container" />
              <div className="eone-ui-modal-footer-button-container">
                {visibleFooterButtons.map((button, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`eone-ui-modal-footer-button ${button.Type === 1 ? 'eone-ui-modal-footer-button-primary' : 'eone-ui-modal-footer-button-secondary'}`}
                    onClick={button.Disabled ? undefined : button.OnClick}
                    disabled={button.Disabled}
                    style={
                      button.Disabled
                        ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    {button.Label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { SCBaseModal, SCModal };
export type { SCConfirmationModalProps, SCModalProps };
